import {
  Ban,
  CheckCheck,
  ChevronRight,
  CircleAlert,
  Clock,
  HardHat,
  Hourglass,
  RefreshCw,
  Send,
  Undo2,
  Upload,
  type LucideIcon,
} from 'lucide-react';
import { createElement } from 'react';
import { Link } from 'react-router';
import { Routes } from '../../app/routes';
import type { RelatedItem } from '../../data/queries';
import type { Item, ItemEvent, WorkKind } from '../../domain/models';
import { awaitsManualResult, eventComment, eventNote, eventReason, eventTaskId, isTask, isWorking } from '../../domain/models';
import { manualText } from '../../domain/payload';
import { useLocale, useT, type Translate } from '../../l10n/i18n';
import { eventLabel, eventStyle, itemKindLabel, statusStyle, timeAgo, toneColors, workKindIcon, type Tone } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { Pill } from '../core/widgets/common';
import { LinkTile, safeUrl } from './links';
import { manualPlan, manualSteps } from './logic';

// The parts of the item page around its content, ported from item_screen.dart.

/** The icon of a kind of work. */
export function KindGlyph({ kind, size }: { kind: WorkKind; size: number }) {
  return createElement(workKindIcon(kind), { size, 'aria-hidden': true });
}

/** On a result: the task it answers, one click away. */
export function ParentLink({ parentId }: { parentId: string }) {
  const t = useT();
  return (
    <Link to={Routes.item(parentId)} className="iv-parent-link">
      <Pill label={t('fromTask')} icon={HardHat} trailing={ChevronRight} tone="accent" />
    </Link>
  );
}

export function ChangeRequestCallout({ comment, note, employee }: { comment: string; note?: string; employee: string }) {
  const t = useT();
  return (
    <div className="panel iv-callout">
      <p className="overline">{t('yourComment')}</p>
      <p className="body-large iv-italic pre-wrap">“{comment}”</p>
      {note && (
        <>
          <p className="overline iv-accent iv-gap-top">{t('whatChanged')}</p>
          <p className="body-medium pre-wrap">
            {employee}: {note}
          </p>
        </>
      )}
    </div>
  );
}

/** Where the item stands once decided, with what the person does next for work they carry out. */
export function StatusBanner({ item }: { item: Item }) {
  const t = useT();
  if (isWorking(item)) return <WorkingBanner name={item.employee.name} />;
  const style = bannerStyle(t, item);
  if (!style) return null;
  const { fg, bg } = toneColors(style.tone);
  const Icon = style.icon;
  const comment = item.decision?.comment;
  const resultUrl = safeUrl(item.result?.link);
  const steps =
    awaitsManualResult(item) && manualText(item.payload) !== undefined
      ? manualSteps(t, item, manualPlan(t, item))
      : [];
  return (
    <div className="iv-banner iv-gap-xl" style={{ background: bg }} role="status">
      <Icon size={20} color={fg} aria-hidden />
      <div className="iv-grow iv-min0">
        <p className="title-small" style={{ color: fg }}>
          {style.text}
        </p>
        {steps.length > 0 && (
          <ol className="iv-steps">
            {steps.map((s, i) => (
              <li key={i}>
                <span className="iv-step-n label-small" style={{ background: fg, color: bg }} aria-hidden>
                  {i + 1}
                </span>
                <span className="body-medium">{s}</span>
              </li>
            ))}
          </ol>
        )}
        {comment && item.status !== 'pending' && <p className="body-medium iv-gap-top pre-wrap">“{comment}”</p>}
        {resultUrl && (
          <div className="iv-gap-top">
            <LinkTile label={t('resultLink')} url={resultUrl} />
          </div>
        )}
        {item.result?.note && <p className="body-medium iv-gap-top pre-wrap">{item.result.note}</p>}
      </div>
    </div>
  );
}

function bannerStyle(t: Translate, item: Item): { text: string; tone: Tone; icon: LucideIcon } | undefined {
  const name = item.employee.name;
  switch (item.status) {
    case 'changes_requested':
      return { text: t('bannerChangesRequested', { name }), tone: 'warning', icon: Hourglass };
    case 'approved':
      if (item.execution === 'source') {
        return { text: t('bannerApprovedSource', { workspace: item.workspace.name }), tone: 'success', icon: RefreshCw };
      }
      if (item.execution === 'manual') {
        const message = item.payload.type === 'message_reply';
        let text: string;
        if (manualText(item.payload) === undefined) text = t('bannerApprovedManualPlain');
        else if (message) text = t('bannerApprovedManualMessage');
        else text = t('bannerApprovedManual');
        return { text, tone: 'warning', icon: message ? Send : Upload };
      }
      return undefined;
    case 'completed':
      return { text: t('bannerCompleted'), tone: 'success', icon: CheckCheck };
    case 'rejected':
      return { text: t('bannerRejected'), tone: 'muted', icon: Ban };
    case 'withdrawn':
      return { text: t('bannerWithdrawn', { name }), tone: 'muted', icon: Undo2 };
    case 'expired':
      return { text: t('bannerExpired'), tone: 'muted', icon: Clock };
    case 'failed':
      return { text: t('bannerFailed'), tone: 'danger', icon: CircleAlert };
    default:
      return undefined;
  }
}

/** "Domas dirba…": the task is in hand, with dots that keep going until the result comes. */
function WorkingBanner({ name }: { name: string }) {
  const t = useT();
  return (
    <div className="iv-banner iv-working iv-gap-xl" role="status">
      <span className="iv-dots" aria-hidden>
        <i />
        <i />
        <i />
      </span>
      <div className="iv-min0">
        <p className="title-small iv-accent">{t('bannerWorking', { name })}</p>
        <p className="body-small">{t('bannerWorkingHint')}</p>
      </div>
    </div>
  );
}

/** The item's trail, newest first. A line about a handoff opens the task it made. */
export function Timeline({ events }: { events: ItemEvent[] }) {
  const t = useT();
  const locale = useLocale();
  const lines = [...events].reverse();
  return (
    <ol className="iv-timeline">
      {lines.map((e, i) => {
        const { icon: Icon, tone } = eventStyle(e);
        const { fg } = toneColors(tone);
        const said = eventComment(e) ?? eventNote(e) ?? eventReason(e);
        const taskId = e.kind === 'handed_off' ? eventTaskId(e) : undefined;
        const body = (
          <>
            <span className="iv-tl-rail">
              <span className="iv-tl-dot" style={{ color: fg, background: `color-mix(in srgb, ${fg} 14%, transparent)` }}>
                <Icon size={15} aria-hidden />
              </span>
              {i < lines.length - 1 && <span className="iv-tl-line" aria-hidden />}
            </span>
            <span className="iv-tl-text">
              <span className="title-small iv-block">{eventLabel(t, locale, e)}</span>
              <time className="body-small iv-block" dateTime={e.createdAt.toISOString()} title={e.createdAt.toLocaleString(locale)}>
                {timeAgo(t, e.createdAt)}
              </time>
              {said && <span className="body-medium muted iv-block iv-tl-said pre-wrap">“{said}”</span>}
            </span>
            {taskId && <ChevronRight size={20} className="faint iv-tl-chevron" aria-hidden />}
          </>
        );
        return (
          <li key={e.id}>
            {taskId ? (
              <Link to={Routes.item(taskId)} className="iv-tl-row iv-tl-link">
                {body}
              </Link>
            ) : (
              <div className="iv-tl-row">{body}</div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** The tasks this item was handed off as, each with its results; or a task's results. */
export function RelatedList({ related }: { related: RelatedItem[] }) {
  return (
    <div className="iv-related">
      {related.map((r) => (
        <div key={r.item.id} className="card iv-related-card">
          <RelatedRow item={r.item} />
          {r.results.map((res) => (
            <RelatedRow key={res.id} item={res} nested />
          ))}
        </div>
      ))}
    </div>
  );
}

function RelatedRow({ item, nested = false }: { item: Item; nested?: boolean }) {
  const t = useT();
  const status = statusStyle(t, item);
  const failure = item.status === 'failed' ? item.result?.note : undefined;
  const subtitle = failure ?? `${itemKindLabel(t, item)} · ${timeAgo(t, item.updatedAt)}`;
  const working = isWorking(item);
  return (
    <Link to={Routes.item(item.id)} className={nested ? 'iv-related-row nested' : 'iv-related-row'}>
      {isTask(item) ? (
        <EmployeeAvatar avatar={item.employee.avatar} color={item.employee.color} size={40} animate={working} label={item.employee.name} />
      ) : (
        <span className={nested ? 'iv-kind-box small accent' : 'iv-kind-box accent'}>
          <KindGlyph kind={item.kind} size={nested ? 15 : 20} />
        </span>
      )}
      <span className="iv-grow iv-min0">
        <span className="title-small ellipsis iv-block">{isTask(item) ? item.employee.name : item.title}</span>
        <span className="body-small iv-clamp" style={failure ? { color: 'var(--danger)' } : undefined}>
          {subtitle}
        </span>
      </span>
      <Pill label={status.label} tone={status.tone} dot={working ? toneColors(status.tone).fg : undefined} pulse={working} />
      <ChevronRight size={20} className="faint" aria-hidden />
    </Link>
  );
}
